import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export interface Account {
  id: string;
  username: string;
  first_name?: string;
  last_name?: string;
  domains?: string[];
  token: string;
  isActive: boolean;
}

interface AccountState {
  accounts: Account[];
  activeAccountId: string | null;
  addAccount: (account: Account) => void;
  removeAccount: (accountId: string) => void;
  setActiveAccount: (accountId: string) => void;
  updateAccount: (accountId: string, updates: Partial<Account>) => void;
  getActiveAccount: () => Account | null;
}

export const useAccountStore = create<AccountState>()(
  persist(
    (set, get) => ({
      accounts: [],
      activeAccountId: null,

      addAccount: (account) => set((state) => {
        // If this is the first account, make it active
        const isFirstAccount = state.accounts.length === 0;
        return {
          accounts: [...state.accounts, { ...account, isActive: isFirstAccount }],
          activeAccountId: isFirstAccount ? account.id : state.activeAccountId
        };
      }),

      removeAccount: (accountId) => set((state) => {
        const newAccounts = state.accounts.filter(acc => acc.id !== accountId);
        // If we removed the active account, make the first remaining account active
        const newActiveId = accountId === state.activeAccountId 
          ? (newAccounts[0]?.id || null)
          : state.activeAccountId;
        
        return {
          accounts: newAccounts,
          activeAccountId: newActiveId
        };
      }),

      setActiveAccount: (accountId) => set((state) => {
        const account = state.accounts.find(acc => acc.id === accountId);
        if (account) {
          // Clear existing session data
          localStorage.clear();
          // Set new session data
          localStorage.setItem('accessToken', account.token);
          localStorage.setItem('user', JSON.stringify({
            id: account.id,
            username: account.username,
            first_name: account.first_name,
            last_name: account.last_name,
            domains: account.domains
          }));
        }
        return {
          accounts: state.accounts.map(acc => ({
            ...acc,
            isActive: acc.id === accountId
          })),
          activeAccountId: accountId
        };
      }),

      updateAccount: (accountId, updates) => set((state) => ({
        accounts: state.accounts.map(acc => 
          acc.id === accountId ? { ...acc, ...updates } : acc
        )
      })),

      getActiveAccount: () => {
        const state = get();
        return state.accounts.find(acc => acc.id === state.activeAccountId) || null;
      }
    }),
    {
      name: 'account-storage',
      // Only persist accounts and activeAccountId
      partialize: (state) => ({ 
        accounts: state.accounts,
        activeAccountId: state.activeAccountId
      })
    }
  )
);
